// EP-134 §7 + §4 (T134-007) — the "Transfer ownership" modal on the unit
// detail page. Calls POST /api/units/:unit_id/owner_transfer (the singular
// noun matches the plan and the route shape) via useOwnerTransfer.
//
// Form shape mirrors the BE payload:
//   { transfer_date: <ISO date>, to: { … } }
//
// The `to:` sub-payload is EITHER { customer_id: <existing> } OR the
// create-new shape (first_name + last_name + phone + email + ...). The
// modal exposes both as a segmented option so the operator picks either
// path; the other is hidden.
//
// §9 Q-5: the confirm step says NOTHING about money. The form has no
// money fields and the success toast names the transfer, not a settlement.
// The tenant-adjustment hint (§9 Q-2) and the committee-seat warning
// (EP-135 §11 D-11a) are inline notes, not blocking dialogs.
import { useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Alert, Modal, Button, Input, SearchableDropdown } from '@/components/ui'
import { useCustomerSearch } from '@/hooks/useCustomers'
import { useOwnerTransfer } from '@/hooks/useOwnerContracts'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useNotificationStore } from '@/stores'
import { useLocalizationConfig } from '@/hooks/useDateFormatter'
import { localToday } from '@/lib/date'

export interface TransferOwnershipModalProps {
  isOpen: boolean
  onClose: () => void
  unitId: number
  /** Outgoing owner display name for the dialog header + warning copy. */
  outgoingOwnerName: string | null
  /** Whether the unit has an active tenant RentalLease — drives the §9 Q-2
   *  tenant-adjustment hint. */
  hasActiveTenant: boolean
  /** Whether the outgoing owner holds a committee seat — drives the
   *  EP-135 §11 D-11a warning. False by default; the committee surface
   *  is built by EP-135. */
  outgoingOwnerHoldsCommitteeSeat?: boolean
  /** Fired after a successful transfer so the parent can refetch the
   *  unit + history. */
  onSuccess?: () => void
}

type IncomingMode = 'create' | 'pick'

interface TransferFormData {
  transfer_date: string
  // create mode fields
  first_name: string
  last_name: string
  phone: string
  email: string
  // pick mode field
  customer_id: string
}

export function TransferOwnershipModal({
  isOpen,
  onClose,
  unitId,
  outgoingOwnerName,
  hasActiveTenant,
  outgoingOwnerHoldsCommitteeSeat = false,
  onSuccess,
}: TransferOwnershipModalProps) {
  const { t } = useTranslation('shared_owners')
  const { t: tc } = useTranslation('common')
  const translateError = useApiErrorTranslator('shared_owners')
  const addToast = useNotificationStore((s) => s.addToast)
  // localToday() takes an IANA timezone, NOT the display date format —
  // passing the format string throws RangeError: Invalid time zone.
  const { timezone } = useLocalizationConfig()

  const [incomingMode, setIncomingMode] = useState<IncomingMode>('create')
  const [pickSearch, setPickSearch] = useState('')
  const [pickedCustomer, setPickedCustomer] = useState<{ id: number; label: string } | null>(null)

  const { control, handleSubmit, register, reset, formState: { errors } } =
    useForm<TransferFormData>({
      defaultValues: {
        transfer_date: localToday(timezone),
        first_name: '',
        last_name: '',
        phone: '',
        email: '',
        customer_id: '',
      },
    })

  // Reset the form on open so a closed-then-reopened modal starts clean
  // (RHF reset is not React state, so this is lint-legal); the three picker
  // states reset in handleClose below instead of synchronously here
  // (react-hooks/set-state-in-effect).
  useEffect(() => {
    if (isOpen) {
      reset({
        transfer_date: localToday(timezone),
        first_name: '',
        last_name: '',
        phone: '',
        email: '',
        customer_id: '',
      })
    }
  }, [isOpen, reset, timezone])

  const handleClose = () => {
    setIncomingMode('create')
    setPickedCustomer(null)
    setPickSearch('')
    onClose()
  }

  const transfer = useOwnerTransfer()
  const { data: searchResults } = useCustomerSearch(pickSearch, { fetchOnEmpty: true })

  const onSubmit = handleSubmit(async (data) => {
    const transfer_date = data.transfer_date
    const to =
      incomingMode === 'pick' && pickedCustomer
        ? { customer_id: pickedCustomer.id }
        : {
            first_name: data.first_name,
            last_name: data.last_name,
            phone: data.phone,
            email: data.email,
          }

    try {
      await transfer.mutateAsync({
        unit_id: unitId,
        transfer_date,
        to,
      })
      addToast({ type: 'success', message: t('messages.transfer.create_success') })
      onSuccess?.()
      handleClose()
    } catch (err) {
      // §4 typed subcodes — translateError maps every TransferError code
      // declared in messages.transfer.errors. Anything else falls through
      // to the generic 422 / 403 / 500 messages.
      const message = translateError(err) ?? tc('errors.something_went_wrong')
      addToast({ type: 'error', message })
    }
  })

  const customerOptions = (searchResults?.data ?? []).map((c) => ({
    value: String(c.id),
    label: c.name,
  }))

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('messages.transfer.title')}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={transfer.isPending}>
            {tc('actions.cancel')}
          </Button>
          <Button
            onClick={onSubmit}
            loading={transfer.isPending}
            data-testid="confirm-transfer"
          >
            {t('messages.transfer.title')}
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        <p className="text-sm text-base-content/70">{t('messages.transfer.description')}</p>

        <Input
          type="date"
          label={t('messages.transfer.transfer_date_label')}
          hint={t('messages.transfer.transfer_date_help')}
          max={localToday(timezone)}
          {...register('transfer_date', { required: t('messages.transfer.errors.transfer_date_required') })}
          error={errors.transfer_date?.message}
        />

        <div>
          <label className="label-text text-sm font-medium block mb-2">
            {t('messages.transfer.to_section_label')}
          </label>
          <div className="tabs tabs-boxed w-fit mb-3">
            <button
              type="button"
              className={incomingMode === 'create' ? 'tab tab-active' : 'tab'}
              onClick={() => setIncomingMode('create')}
              data-testid="transfer-mode-create"
            >
              {t('messages.transfer.to_create_option')}
            </button>
            <button
              type="button"
              className={incomingMode === 'pick' ? 'tab tab-active' : 'tab'}
              onClick={() => setIncomingMode('pick')}
              data-testid="transfer-mode-pick"
            >
              {t('messages.transfer.to_pick_option')}
            </button>
          </div>

          {incomingMode === 'create' ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label={t('labels.first_name')}
                  {...register('first_name')}
                  error={errors.first_name?.message}
                />
                <Input
                  label={t('labels.last_name')}
                  {...register('last_name')}
                  error={errors.last_name?.message}
                />
              </div>
              <Input
                label={t('labels.phone')}
                placeholder={t('placeholders.phone')}
                required
                {...register('phone', {
                  required: t('messages.transfer.errors.buyer_phone_required'),
                })}
                error={errors.phone?.message}
              />
              <Input
                type="email"
                label={t('labels.email')}
                placeholder={t('placeholders.email')}
                {...register('email')}
                error={errors.email?.message}
              />
            </div>
          ) : (
            <Controller
              control={control}
              name="customer_id"
              rules={{ required: t('messages.transfer.errors.buyer_required') }}
              render={({ field }) => (
                <SearchableDropdown
                  label={t('messages.transfer.pick_customer_label')}
                  options={customerOptions}
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value)
                    const found = customerOptions.find((o) => o.value === value)
                    setPickedCustomer(found ? { id: Number(found.value), label: found.label } : null)
                  }}
                  onSearch={setPickSearch}
                  error={errors.customer_id?.message}
                />
              )}
            />
          )}
        </div>

        {/* §9 Q-2 — the rental lease is NOT touched by the transfer; the
            operator adjusts it by hand. We surface that as a hint so the
            operator does not expect automation. */}
        {hasActiveTenant && (
          <Alert variant="info">{t('messages.transfer.tenant_warning')}</Alert>
        )}

        {/* EP-135 §11 D-11a — selling a unit does not auto-vacate a
            committee seat; warn the operator so they do not discover it
            at the next meeting. */}
        {outgoingOwnerHoldsCommitteeSeat && (
          <Alert variant="warning">{t('messages.transfer.committee_warning')}</Alert>
        )}

        {outgoingOwnerName && (
          <p className="text-sm text-base-content/60">
            {outgoingOwnerName}
          </p>
        )}
      </form>
    </Modal>
  )
}